import { useState, useEffect, useMemo, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/auth";
import { useToast } from "@/hooks/use-toast";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  CheckCircle,
  XCircle,
  Users,
  UserCheck,
  UserX,
  Save,
  X,
  Edit,
  Calendar as CalendarIcon,
  BookOpen,
  GraduationCap,
  UserMinus,
  TrendingUp,
  UserPlus,
  AlertTriangle,
  List,
} from "lucide-react";
import { ArabicCalendar } from "@/components/ui/arabic-calendar";
import { useSimpleAttendanceState } from "@/hooks/useSimpleAttendanceState";
import { toArabicNumbers } from "@/utils/arabicNumbers";
import {
  WeekendIndicator,
  WeekendWarningDialog,
} from "@/components/attendance/shared";
import { useWeekendCheck } from "@/hooks/attendance/useWeekendCheck";
import { useRealSchoolSettings } from "@/hooks/useRealSchoolSettings";
import { filterAndSortStudents } from "@/utils/studentSorting";
import { useRealAttendance, useRealStudents } from "@/hooks";
import { StudentWithAttendance } from "@/types/attendance";
import { LateAttendanceControls } from "@/components/attendance/late/LateAttendanceControls";

interface Student {
  id: string;
  name: string;
  grade: string;
  section: string;
}
type AttendanceStatus = "present" | "absent" | "absent_with_reason";

interface AttendanceRecord {
  absent_reason?: string;
  id?: string;
  student_id: string;
  date: string;
  status: AttendanceStatus;
  period: number;
  school_id: string;
  recorded_by: string;
}

const getStudentStatus = (
  student: StudentWithAttendance,
  localChanges: { [studentId: string]: AttendanceStatus }
): AttendanceStatus | undefined => {
  const studentId = student.id;
  if (localChanges[studentId]) {
    return localChanges[studentId];
  }

  return student?.status as AttendanceStatus | undefined;
};

const SimpleAttendanceFixed = () => {
  const [localChanges, setLocalChanges] = useState<{
    [studentId: string]: AttendanceStatus;
  }>({});

  const [absentReasons, setAbsentReasons] = useState<{
    [studentId: string]: string;
  }>({});
  const [isEditMode, setIsEditMode] = useState(false);
  // const [loading, setLoading] = useState(false);
  const [showUnmarkedDialog, setShowUnmarkedDialog] = useState(false);
  const [unmarkedStudents, setUnmarkedStudents] = useState<string[]>([]);
  const { settings } = useRealSchoolSettings();
  const { useAttendanceByDate, getStudentAttendance, recordBulkAttendance } =
    useRealAttendance();
  // const { students } = useRealStudents();

  const { user } = useAuth();
  const { toast } = useToast();

  // خطافات للتحقق من أيام الإجازة
  const {
    showWeekendWarning,
    checkWeekendAndProceed,
    handleWeekendConfirm,
    handleWeekendCancel,
  } = useWeekendCheck();

  // استخدام hook لحالة الحضور مع إعدادات الصفوف
  const {
    selectedGrade,
    selectedStage,
    getStageDisplayName,
    setSelectedStage,
    selectedSection,
    selectedDate,
    availableGrades,
    availableSections,
    // studentsWithAttendance,
    setSelectedGrade,
    setSelectedSection,
    setSelectedDate,
    enabledStages,
    students,
    hasMultipleStages,
  } = useSimpleAttendanceState();

  // const [attendanceRecords, setAttendanceRecords] = useState<
  //   AttendanceRecord[]
  // >([]);

  const [studentsWithAttendance, setStudentsWithAttendance] = useState<
    StudentWithAttendance[]
  >([]);

  const { data: attendanceRecords, isLoading: loading } = useAttendanceByDate(
    selectedDate,
    1
  );

  // console.log("studentsWithAttendance", studentsWithAttendance);
  // console.log("attendanceRecords", attendanceRecords);

  useEffect(() => {
    const refreshData = () => {
      // console.log(" call refreshData() ");
      if (!students || students.length === 0) {
        return;
      }

      // console.log("🔄 Force refreshing attendance data...");

      try {
        // معالجة بيانات الحضور مع البيانات الجديدة
        const studentsWithAttendanceData = students.map((student) => {
          const attendanceRecord =
            attendanceRecords?.find((r) => r?.student_id === student.id) ||
            undefined;

          return {
            ...student,
            status: (attendanceRecord?.status === "excused"
              ? "absent"
              : attendanceRecord?.status) as
              | "present"
              | "absent"
              | "absent_with_reason"
              | "late"
              | undefined,
            isPresent:
              attendanceRecord?.status === "present" ||
              attendanceRecord?.status === "late",
            absenceDays: 0,
            lateDays: 0,
            attendancePercentage: 0,
          };
        });

        setStudentsWithAttendance(studentsWithAttendanceData);
      } catch (error) {
        console.error("❌ Error in force refresh:", error);
      }
    };
    refreshData();
    setLocalChanges({});
    setIsEditMode(false);
  }, [attendanceRecords, students]);

  const checkUnmarkedStudents = () => {
    const unmarked = studentsWithAttendance.filter((student) => {
      const status = getStudentStatus(student, localChanges);
      return !status; // إذا لم يكن له حالة (present أو absent)
    });

    if (unmarked.length > 0) {
      setUnmarkedStudents(unmarked.map((s) => s.name));
      setShowUnmarkedDialog(true);
      return false; // منع الحفظ
    }
    return true; // يمكن الحفظ
  };

  const handleAbsentReasonChange = (studentId: string, reason: string) => {
    setAbsentReasons((prev) => ({
      ...prev,
      [studentId]: reason,
    }));
  };
  const saveChanges = async () => {
    if (!user || Object.keys(localChanges).length === 0) return;

    try {
      // setLoading(true);

      const dateString = selectedDate;

      const records = Object.entries(localChanges).map(
        ([studentId, status]) => ({
          studentId: studentId,
          date: dateString,
          status,
          // period: 1,
          // school_id: settings?.school_id ?? null,
          // recorded_by: user.id,
          absent_reason:
            status === "absent_with_reason"
              ? absentReasons[studentId] || ""
              : null,
        })
      );

      const data = await recordBulkAttendance(records);

      // console.log("Saved attendance records:", data);
      const updated = studentsWithAttendance.map((record) => {
        const rec = data.find(
          (student) => student.student_id === record.studentId
        );
        // Merge and ensure status is not "excused"
        const merged = { ...record, ...rec } as StudentWithAttendance;

        return merged;
      });
      setStudentsWithAttendance(updated);

      setLocalChanges({});
      setIsEditMode(false);
      toast({
        title: "تم الحفظ",
        description: `تم حفظ الحضور لـ ${
          Object.keys(localChanges).length
        } طالب`,
        variant: "default",
      });
    } catch (error) {
      console.error("Error saving attendance:", error);
      toast({
        title: "خطأ في الحفظ",
        description: error?.message || "فشل في حفظ البيانات",
        variant: "destructive",
      });
    } finally {
      // setLoading(false);
    }
  };

  // دالة للحفظ مع التحقق من الطلاب غير المسجلين
  const handleSaveWithCheck = () => {
    if (checkUnmarkedStudents()) {
      saveChanges();
    }
  };

  // دالة للحفظ بدون تحقق (عند التأكيد من المستخدم)
  const handleForceSave = () => {
    setShowUnmarkedDialog(false);
    saveChanges();
  };

  const updateStudentStatus = (studentId: string, status: AttendanceStatus) => {
    setLocalChanges((prev) => ({
      ...prev,
      [studentId]: status,
    }));
  };

  const markAllPresent = () => {
    const changes: { [studentId: string]: AttendanceStatus } = {};
    studentsWithAttendance.forEach((student) => {
      changes[student.id] = "present";
    });
    setLocalChanges(changes);
  };

  const markAllAbsent = () => {
    const changes: { [studentId: string]: AttendanceStatus } = {};
    studentsWithAttendance.forEach((student) => {
      changes[student.id] = "absent";
    });
    setLocalChanges(changes);
  };

  const markAllAbsentWithResoan = () => {
    const changes: { [studentId: string]: AttendanceStatus } = {};
    studentsWithAttendance.forEach((student) => {
      changes[student.id] = "absent_with_reason";
    });
    setLocalChanges(changes);
  };
  const cancelEdit = () => {
    setLocalChanges({});
    setIsEditMode(false);
  };

  // إحصائيات الحضور
  const stats = useMemo(() => {
    const total = studentsWithAttendance.length;
    let present = 0;
    let absent = 0;
    let withResoan = 0;

    studentsWithAttendance.forEach((student) => {
      const status = getStudentStatus(student, localChanges);
      if (status === "present") present++;
      else if (status === "absent") absent++;
      else if (status === "absent_with_reason") withResoan++;
    });

    return {
      total,
      present,
      absent,
      notMarked: total - present - absent - withResoan,
      withReason: withResoan,
    };
  }, [localChanges, studentsWithAttendance]);

  // فحص إذا كان هناك حضور مسجل للطلاب المعروضين
  const hasAttendanceRecords = useMemo(() => {
    if (!studentsWithAttendance.length) return false;

    return studentsWithAttendance.some((student) => {
      const status = getStudentStatus(student, localChanges);
      return (
        status === "present" ||
        status === "absent" ||
        status == "absent_with_reason"
      );
    });
  }, [localChanges, studentsWithAttendance]);

  const handleDateChange = (date: Date) => {
    const dateString = date.toISOString().split("T")[0];
    const formattedDate = dateFormatter(date);
    setSelectedDate(formattedDate);
    setLocalChanges({});
    setIsEditMode(false);
  };

  const dateFormatter = (date: Date) => {
    const pad = (n: number) => n.toString().padStart(2, "0");
    const yyyy = date.getFullYear();
    const mm = pad(date.getMonth() + 1);
    const dd = pad(date.getDate());
    const formatted = `${yyyy}-${mm}-${dd}`;
    return formatted;
  };

  return (
    <div className="container mx-auto p-4" dir="rtl">
      <LateAttendanceControls
        title="تسجيل الحضور"
        description="تسجيل حضور الطلاب اليومي"
        selectedDate={selectedDate}
        selectedStage={selectedStage}
        selectedGrade={selectedGrade}
        selectedSection={selectedSection}
        hasMultipleStages={hasMultipleStages}
        enabledStages={enabledStages}
        availableGrades={availableGrades}
        availableSections={availableSections}
        // loading={loading}
        onDateChange={setSelectedDate}
        onStageChange={setSelectedStage}
        onGradeChange={setSelectedGrade}
        onSectionChange={setSelectedSection}
        getStageDisplayName={getStageDisplayName}
      />

      {/* إحصائيات */}
      {students?.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6 mt-8">
          {/* إجمالي الطلاب */}
          <div className="relative bg-white rounded-lg p-3 shadow-md border-r-4 border-emerald-500 min-h-[60px]">
            <div className="flex items-center gap-2 w-full">
              <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Users className="w-4 h-4 text-emerald-600" />
              </div>
              <div className="flex-1 text-center">
                <p className="text-gray-600 text-xs font-medium whitespace-nowrap">
                  إجمالي الطلاب
                </p>
              </div>
              <div className="flex-shrink-0">
                <p className="text-lg font-bold text-emerald-600">
                  {toArabicNumbers(stats.total.toString())}
                </p>
              </div>
            </div>
          </div>

          {/* معدل الحضور */}
          <div className="relative bg-white rounded-lg p-3 shadow-md border-r-4 border-cyan-500 min-h-[60px]">
            <div className="flex items-center gap-2 w-full">
              <div className="w-8 h-8 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-4 h-4 text-cyan-600" />
              </div>
              <div className="flex-1 text-center">
                <p className="text-gray-600 text-xs font-medium whitespace-nowrap">
                  معدل الحضور
                </p>
              </div>
              <div className="flex-shrink-0">
                <p className="text-lg font-bold text-cyan-600">
                  {stats.total > 0
                    ? toArabicNumbers(
                        Math.round(
                          (stats.present / stats.total) * 100
                        ).toString()
                      )
                    : toArabicNumbers("0")}
                  %
                </p>
              </div>
            </div>
          </div>

          {/* الحاضرون */}
          <div className="relative bg-white rounded-lg p-3 shadow-md border-r-4 border-green-500 min-h-[60px]">
            <div className="flex items-center gap-2 w-full">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <UserCheck className="w-4 h-4 text-green-600" />
              </div>
              <div className="flex-1 text-center">
                <p className="text-gray-600 text-xs font-medium whitespace-nowrap">
                  حاضر
                </p>
              </div>
              <div className="flex-shrink-0">
                <p className="text-lg font-bold text-green-600">
                  {toArabicNumbers(stats.present.toString())}
                </p>
              </div>
            </div>
          </div>

          {/* الغائبون */}
          <div className="relative bg-white rounded-lg p-3 shadow-md border-r-4 border-red-500 min-h-[60px]">
            <div className="flex items-center gap-2 w-full">
              <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <UserMinus className="w-4 h-4 text-red-600" />
              </div>
              <div className="flex-1 text-center">
                <p className="text-gray-600 text-xs font-medium whitespace-nowrap">
                  غائب
                </p>
                <div>
                  <span className="text-xs text-foreground/70 font-medium">
                    الغائبون بعذر :
                  </span>
                  <span className="text-sm text-foreground/70 font-medium">
                    {toArabicNumbers(stats.withReason.toString())}
                  </span>{" "}
                </div>{" "}
              </div>
              <div className="flex-shrink-0">
                <p className="text-lg font-bold text-red-600">
                  {toArabicNumbers(
                    (stats.absent + stats.withReason).toString()
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedGrade && selectedSection && (
        <>
          {/* قائمة الطلاب */}
          <Card className="bg-white/95 backdrop-blur-sm border-white/30 shadow-lg">
            <CardHeader
              className="rounded-t-lg p-4 sm:p-6 text-white"
              style={{ backgroundColor: "#11a7a2" }}
            >
              <div className="flex justify-between items-center flex-wrap gap-2">
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <List className="h-5 w-5" />
                  قائمة طلاب {selectedGrade} - {selectedSection}
                </CardTitle>

                <div className="flex gap-2 flex-wrap">
                  {!isEditMode ? (
                    <Button
                      onClick={() => setIsEditMode(true)}
                      className="bg-white/20 text-white border border-white/30 hover:bg-white/30 flex items-center gap-2"
                    >
                      {hasAttendanceRecords ? (
                        <>
                          <Edit className="h-4 w-4" />
                          <span>تعديل</span>
                        </>
                      ) : (
                        <>
                          <UserPlus className="h-4 w-4" />
                          <span>تسجيل الحضور</span>
                        </>
                      )}
                    </Button>
                  ) : (
                    <>
                      <Button
                        onClick={markAllPresent}
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <UserCheck className="h-4 w-4 ml-1" />
                        الكل حاضر
                      </Button>
                      <Button
                        onClick={markAllAbsent}
                        size="sm"
                        className="bg-red-600 hover:bg-red-700 text-white"
                      >
                        <UserX className="h-4 w-4 ml-1" />
                        الكل غائب
                      </Button>
                      <Button
                        onClick={markAllAbsentWithResoan}
                        size="sm"
                        className="bg-red-600 hover:bg-red-700 text-white"
                      >
                        <UserX className="h-4 w-4 ml-1" />
                        الكل غائب بعذر
                      </Button>
                      <Button
                        onClick={() =>
                          checkWeekendAndProceed(
                            selectedDate,
                            handleSaveWithCheck
                          )
                        }
                        disabled={
                          Object.keys(localChanges).length === 0 || loading
                        }
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        <Save className="h-4 w-4 ml-1" />
                        حفظ ({Object.keys(localChanges).length})
                      </Button>
                      <Button
                        onClick={cancelEdit}
                        className="bg-white/20 text-white border border-white/30 hover:bg-white/30"
                      >
                        <X className="h-4 w-4 ml-1" />
                        إلغاء
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="overflow-x-auto pb-4">
                <table className="w-full border-collapse border-2 border-r-2 border-b-2 border-gray-300 rounded-lg overflow-hidden">
                  <thead>
                    <tr className="border-b border-gray-300 bg-gray-200">
                      <th className="text-right p-4 font-medium text-gray-700 w-16 border-l border-r border-gray-300">
                        م
                      </th>
                      <th className="text-right p-4 font-medium text-gray-700 border-l border-gray-300">
                        اسم الطالب
                      </th>
                      <th className="text-center p-4 font-medium text-gray-700 w-48">
                        حالة الحضور
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {studentsWithAttendance.map((student, index) => {
                      const status = getStudentStatus(student, localChanges);
                      return (
                        <tr
                          key={student.id}
                          className={`${
                            index === studentsWithAttendance.length - 1
                              ? "border-b-2 border-gray-300"
                              : "border-b border-gray-300"
                          } hover:bg-gray-50 ${
                            status === "present"
                              ? "bg-green-50/50"
                              : status === "absent"
                              ? "bg-red-50/50"
                              : "bg-white"
                          }`}
                        >
                          <td className="p-4 text-right text-gray-600 font-medium border-l border-r border-gray-300">
                            {toArabicNumbers((index + 1).toString())}
                          </td>
                          <td className="p-4 text-right font-medium text-gray-800 border-l border-gray-300">
                            {student.name}
                          </td>
                          <td className="p-4 text-center border-r border-gray-300">
                            {isEditMode ? (
                              <div className="flex gap-2 justify-center">
                                <Button
                                  size="sm"
                                  variant={
                                    status === "present" ? "default" : "outline"
                                  }
                                  onClick={() =>
                                    updateStudentStatus(student.id, "present")
                                  }
                                  className={
                                    status === "present"
                                      ? "bg-green-600 hover:bg-green-700 text-white"
                                      : "border-green-600 text-green-600 hover:bg-green-50"
                                  }
                                >
                                  <CheckCircle className="h-4 w-4 ml-1" />
                                  حاضر
                                </Button>
                                <Button
                                  size="sm"
                                  variant={
                                    status === "absent" ? "default" : "outline"
                                  }
                                  onClick={() =>
                                    updateStudentStatus(student.id, "absent")
                                  }
                                  className={
                                    status === "absent"
                                      ? "bg-red-600 hover:bg-red-700 text-white"
                                      : "border-red-600 text-red-600 hover:bg-red-50"
                                  }
                                >
                                  <XCircle className="h-4 w-4 ml-1" />
                                  غائب
                                </Button>{" "}
                                <Button
                                  size="sm"
                                  variant={
                                    status === "absent_with_reason"
                                      ? "default"
                                      : "outline"
                                  }
                                  onClick={() =>
                                    updateStudentStatus(
                                      student.id,
                                      "absent_with_reason"
                                    )
                                  }
                                  className={
                                    status === "absent_with_reason"
                                      ? "bg-red-600 hover:bg-red-700 text-white"
                                      : "border-red-600 text-red-600 hover:bg-red-50"
                                  }
                                >
                                  <XCircle className="h-4 w-4 ml-1" />
                                  غائب بعذر
                                </Button>
                                {status === "absent_with_reason" && (
                                  <input
                                    type="text"
                                    className="ml-2 border rounded px-2 py-1 text-sm"
                                    placeholder="سبب الغياب"
                                    value={
                                      absentReasons[student.id] !== undefined
                                        ? absentReasons[student.id]
                                        : attendanceRecords.find(
                                            (r) =>
                                              r.student_id === student.id &&
                                              r.date === selectedDate &&
                                              r.period === 1
                                          )?.absent_reason || ""
                                    }
                                    onChange={(e) =>
                                      handleAbsentReasonChange(
                                        student.id,
                                        e.target.value
                                      )
                                    }
                                    style={{ minWidth: "120px" }}
                                  />
                                )}
                              </div>
                            ) : (
                              <div className="flex justify-center">
                                {status === "present" && (
                                  <div className="flex items-center bg-green-100 text-green-800 px-3 py-1 rounded-full border border-green-200">
                                    <CheckCircle className="h-4 w-4 ml-1" />
                                    حاضر
                                  </div>
                                )}
                                {status === "absent" && (
                                  <div className="flex items-center bg-red-100 text-red-800 px-3 py-1 rounded-full border border-red-200">
                                    <XCircle className="h-4 w-4 ml-1" />
                                    غائب
                                  </div>
                                )}
                                {status === "absent_with_reason" && (
                                  <div className="flex flex-col items-center">
                                    <div className="flex items-center bg-red-100 text-red-800 px-3 py-1 rounded-full border border-red-200 w-fit">
                                      <XCircle className="h-4 w-4 ml-1" />
                                      غائب بعذر
                                    </div>
                                    <span className="text-xs text-gray-700 mt-1">
                                      {attendanceRecords?.find(
                                        (r) =>
                                          r.student_id === student.id &&
                                          r.date === selectedDate
                                      )?.absent_reason || ""}
                                    </span>
                                  </div>
                                )}
                                {!status && (
                                  <div className="flex items-center bg-gray-100 text-gray-600 px-3 py-1 rounded-full border border-gray-200">
                                    غير محدد
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* حوار تحذير يوم الإجازة */}
      <WeekendWarningDialog
        open={showWeekendWarning}
        onOpenChange={() => {}}
        onConfirm={handleWeekendConfirm}
        onCancel={handleWeekendCancel}
        date={selectedDate}
        type="attendance"
      />

      {/* حوار تنبيه الطلاب غير المسجلين */}
      <AlertDialog
        open={showUnmarkedDialog}
        onOpenChange={setShowUnmarkedDialog}
      >
        <AlertDialogContent className="max-w-md" dir="rtl">
          <AlertDialogHeader className="bg-orange-50 -mx-6 -mt-6 px-6 pt-6 pb-4 rounded-t-lg">
            <AlertDialogTitle className="flex items-center gap-2 text-orange-600">
              <AlertTriangle className="h-5 w-5" />
              تنبيه - طلاب غير مسجلين
            </AlertDialogTitle>
          </AlertDialogHeader>
          <div className="-mx-6">
            <AlertDialogDescription className="space-y-3 text-gray-700 px-6">
              <p className="text-sm text-right">
                يوجد {unmarkedStudents.length} طالب غير مسجل:
              </p>
              <div className="bg-orange-50 p-3 rounded-lg max-h-32 overflow-y-auto mx-0">
                <ul className="text-sm text-orange-800 text-right">
                  {unmarkedStudents.map((name, index) => (
                    <li key={index} className="py-1">
                      • {name}
                    </li>
                  ))}
                </ul>
              </div>
              <p className="text-sm text-right">
                هل تريد المتابعة وحفظ الحضور كما هو؟
              </p>
            </AlertDialogDescription>

            <AlertDialogFooter className="gap-2 mt-6 px-6">
              <AlertDialogCancel
                onClick={() => setShowUnmarkedDialog(false)}
                className="bg-gray-100 hover:bg-gray-200"
              >
                رجوع
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleForceSave}
                className="bg-orange-600 hover:bg-orange-700"
              >
                نعم، متابعة الحفظ
              </AlertDialogAction>
            </AlertDialogFooter>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SimpleAttendanceFixed;
